import { useMemo } from "react";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid";
import { getTollReport } from "@/api/report/getTollReport";
import type { TollReportRow } from "@/api/report/_models/TollReport";

/** Grid columns from the union of the rows' `fields` keys, in first-seen order (the Toll export's columns vary). */
const buildColumns = (rows: TollReportRow[]): GridColDef<TollReportRow>[] => {
  const keys: string[] = [];
  for (const row of rows) {
    for (const key of Object.keys(row.fields)) {
      if (!keys.includes(key)) {
        keys.push(key);
      }
    }
  }

  return keys.map((key) => ({
    field: key,
    headerName: key,
    width: 160,
    valueGetter: (_value, row: TollReportRow) => row.fields[key],
  }));
};

export function TollReport() {
  const query = getTollReport.useQuery();
  const rows = query.data ?? [];
  const columns = useMemo(() => buildColumns(rows), [rows]);

  if (query.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (query.isError) {
    return (
      <Alert severity="error">
        Failed to load Toll report: {JSON.stringify(query.error)}
      </Alert>
    );
  }

  return (
    <Box sx={{ height: "calc(100vh - 160px)", width: "100%" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        density="compact"
        showToolbar
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 50 } },
        }}
        disableRowSelectionOnClick
      />
    </Box>
  );
}
